/**
 * @author michalstolarski
 * @since 2022-3-25
 */

import React from "react";
import styled, { css } from "styled-components";

import {
  SWIPE_STATUS,
  SLIDER_ACTION_TYPE,
  sliderReducer,
  listenerHandler,
  getClientPos,
} from "./FadeInSlider.helpers";
import { FadeInDots } from "./FadeInDots";

interface SliderSettings {
  /** starting from `0` - default `0` */
  initialSlide?: number;
  /** show/hide dots - default `true` */
  dots?: boolean;
  /** animation speed - default `200` milliseconds */
  speed?: number;
  /** delay between fade-in and fade-out animation - default `0` milliseconds */
  delay?: number;
  /** number of pixels that user has to swipe to change slide - default 50 pixels */
  swipeThresholdInPx?: number;
}

interface Props extends SliderSettings {
  children: React.ReactNode;
  className?: string;
  /** extra styles for the dots container */
  dotsStyle?: React.CSSProperties;
}

/**
 * Slider with the fade-in/out animation
 */
const FadeInSlider = ({
  initialSlide = 0,
  dots = true,
  speed = 200,
  delay = 0,
  ...props
}: Props): React.ReactElement => {
  const ref = React.useRef<HTMLUListElement | null>(null);
  const [startPos, setStartPos] = React.useState<number | null>(null);
  const [endPos, setEndPos] = React.useState<number | null>(null);
  const [swipeStatus, setSwipeStatus] = React.useState<SWIPE_STATUS>(
    SWIPE_STATUS.IDLE,
  );
  const [sliderState, sliderStateDispatch] = React.useReducer(sliderReducer, {
    current: initialSlide as number,
    size: React.Children.count(props.children),
  });

  const goToHandler = (goTo: number) =>
    sliderStateDispatch({
      type: SLIDER_ACTION_TYPE.GO_TO,
      payload: goTo,
    });

  React.useEffect(() => {
    const onMove = (e: TouchEvent | MouseEvent): void => {
      setEndPos(getClientPos(e));
    };

    const onStart = (e: TouchEvent | MouseEvent): void => {
      setSwipeStatus(SWIPE_STATUS.START);
      setStartPos(getClientPos(e));

      listenerHandler.add(["touchmove", "mousemove"], document, onMove);
    };

    const onEnd = (): void => {
      setSwipeStatus(SWIPE_STATUS.END);

      listenerHandler.remove(["touchmove", "mousemove"], document, onMove);
    };

    listenerHandler.add(["touchstart", "mousedown"], ref.current, onStart);
    listenerHandler.add(["touchend", "mouseup"], document, onEnd);

    return () => {
      listenerHandler.remove(["touchstart", "mousedown"], ref.current, onStart);
      listenerHandler.remove(["touchmove", "mousemove"], document, onMove);
      listenerHandler.remove(["touchend", "mouseup"], document, onEnd);
    };
  }, [ref]);

  React.useEffect(() => {
    if (swipeStatus !== SWIPE_STATUS.END) return;
    if (startPos === null || endPos === null) return;

    const diff = startPos - endPos;

    if (Math.abs(diff) > (props.swipeThresholdInPx || 50)) {
      if (diff > 0) {
        sliderStateDispatch({ type: SLIDER_ACTION_TYPE.NEXT });
      } else {
        sliderStateDispatch({ type: SLIDER_ACTION_TYPE.PREV });
      }
    }

    setEndPos(null);
    setStartPos(null);
    setSwipeStatus(SWIPE_STATUS.IDLE);
  }, [swipeStatus]);

  return (
    <React.Fragment>
      <Slider ref={ref} className={props.className}>
        {React.Children.map(props.children, (child, i) => {
          if (!React.isValidElement(child)) {
            return child;
          }

          return (
            <Slide
              speed={speed as number}
              delay={delay as number}
              isActive={i === sliderState.current}
            >
              {React.cloneElement(child, {})}
            </Slide>
          );
        })}
      </Slider>

      {dots && (
        <FadeInDots
          style={props.dotsStyle}
          sliderSize={sliderState.size}
          currentSlide={sliderState.current}
          onChange={goToHandler}
        />
      )}
    </React.Fragment>
  );
};

export { FadeInSlider };

const Slider = styled.ul`
  position: relative;
  width: 100%;
  margin: 0;
  padding: 0;
  list-style: none;

  &,
  * {
    overscroll-behavior: contain;
    touch-action: pan-y;
  }

  img {
    pointer-events: none;
  }
`;

const Slide = styled.li<{ speed: number; delay: number; isActive: boolean }>`
  position: absolute;
  inset: 0;
  height: 100%;
  width: 100%;
  transition: opacity ${props => props.speed}ms ease-in;
  opacity: 0;
  pointer-events: none;
  cursor: pointer;
  z-index: 1;

  ${props =>
    props.isActive &&
    css`
      position: relative;
      transition: opacity ${props.speed}ms ${props.speed + props.delay}ms
        ease-out;
      opacity: 1;
      pointer-events: all;
    `}
`;
